import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { duplicationLaunchSchema, type DuplicationLaunch } from "@shared/ads-manager-duplication-engine";
import { currencyPrecision } from "./ads-manager-duplication";

export type PendingDuplicationAdmission = { brandId: string; currency: string; payload: DuplicationLaunch };
const changedEvent = "ads-manager-duplication-admission-changed";
const storageKey = (actorId: string) => `ads-manager-unresolved-admission:${actorId}`;

export function readPendingAdmission(actorId: string | null): { pending: PendingDuplicationAdmission | null; error: string | null } {
  if (!actorId) return { pending: null, error: "Wait for your signed-in identity before submitting." };
  try {
    const text = localStorage.getItem(storageKey(actorId));
    if (!text) return { pending: null, error: null };
    const record = JSON.parse(text);
    const parsed = duplicationLaunchSchema.safeParse(record.payload);
    if (!parsed.success || typeof record.brandId !== "string" || !record.brandId || currencyPrecision(record.currency) === null)
      throw new Error("Invalid admission record");
    return { pending: { brandId: record.brandId, currency: record.currency, payload: parsed.data }, error: null };
  } catch {
    return { pending: null, error: "A saved duplication admission cannot be read. Do not start a replacement run; check Ads Manager." };
  }
}

export function savePendingAdmission(actorId: string | null, pending: PendingDuplicationAdmission) {
  if (!actorId) throw new Error("Your signed-in identity is unavailable.");
  const prior = readPendingAdmission(actorId);
  if (prior.error) throw new Error(prior.error);
  if (prior.pending && JSON.stringify(prior.pending) !== JSON.stringify(pending))
    throw new Error("A prior duplication admission still needs confirmation. Do not start a replacement run.");
  try {
    localStorage.setItem(storageKey(actorId), JSON.stringify(pending));
    if (localStorage.getItem(storageKey(actorId)) !== JSON.stringify(pending)) throw new Error("Admission identity was not saved.");
  } catch {
    throw new Error("Duplication request identity could not be saved. No replacement request was sent.");
  }
  window.dispatchEvent(new Event(changedEvent));
}

export function clearPendingAdmission(actorId: string | null, requestKey: string) {
  if (!actorId) return;
  const prior = readPendingAdmission(actorId);
  if (prior.pending?.payload.requestKey !== requestKey) return;
  localStorage.removeItem(storageKey(actorId));
  window.dispatchEvent(new Event(changedEvent));
}

export function usePendingDuplicationAdmission() {
  const auth = useQuery<{ id: string } | null>({ queryKey: ["/api/auth/user"], enabled: false });
  const actorId = auth.data?.id ?? null;
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const update = () => setRevision(v => v + 1);
    window.addEventListener(changedEvent, update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener(changedEvent, update);
      window.removeEventListener("storage", update);
    };
  }, []);
  return useMemo(() => ({ actorId, ...readPendingAdmission(actorId) }), [actorId, revision]);
}
